// NOTE: Mirrors apps/nsai/server/src/notifications/notifications.types.ts.
// Keep both sides in sync — the wire shape between server and client lives here.

import type { EConnectorApprovalLevel } from "./connector-approval";
import type { TEntityId } from "./entity-id";
import type { EIntegrationProvider } from "./integration";
import type { EMeetingStatus } from "./meetings";
import type { TTokenLimitPeriod } from "./organizations";
import type {
  ETokenBudgetScope,
  ETokenBudgetUnit,
  ETokenBudgetUsageKind,
} from "./token-budget";

export enum ENotificationType {
  PROJECT_INVITE = "project_invite",
  PROJECT_LEFT = "project_left",
  PROJECT_MEMBER_REMOVED = "project_member_removed",
  PROJECT_ROLE_UPDATED = "project_role_updated",
  SELF_REMINDER = "self_reminder",
  MEETING_PROCESSED = "meeting_processed",
  MEETING_FAILED = "meeting_failed",
  MEETING_SHARED = "meeting_shared",
  MEETING_ACCESS_REVOKED = "meeting_access_revoked",
  CALENDAR_DISCONNECTED = "calendar_disconnected",
  INTEGRATION_DISCONNECTED = "integration_disconnected",
  MODEL_CREATED = "model_created",
  KNOWLEDGE_FILE_SHARED = "knowledge_file_shared",
  KNOWLEDGE_FILE_ACCESS_REVOKED = "knowledge_file_access_revoked",
  PROMPT_CREATED = "prompt_created",
  PROBE_CREATED = "probe_created",
  ASSISTANT_CREATED = "assistant_created",
  ASSISTANT_SHARED = "assistant_shared",
  ASSISTANT_LEFT = "assistant_left",
  ASSISTANT_PERMISSION_CHANGED = "assistant_permission_changed",
  ASSISTANT_ACCESS_REVOKED = "assistant_access_revoked",
  ASSISTANT_EDITED = "assistant_edited",
  // NOTE: NSAI-6952 — Agent Skills: the ASSISTANT_* set minus a "left" event.
  // A recipient CAN leave a share (NSAI-6578), but that deliberately notifies
  // nobody, so there is no type for it.
  SKILL_CREATED = "skill_created",
  SKILL_SHARED = "skill_shared",
  SKILL_PERMISSION_CHANGED = "skill_permission_changed",
  SKILL_ACCESS_REVOKED = "skill_access_revoked",
  SKILL_EDITED = "skill_edited",
  CHAT_SHARED = "chat_shared",
  CHAT_ACCESS_REVOKED = "chat_access_revoked",
  // NOTE: NSAI-6940 — a dashboard shared with one project member: the share, a
  // change of its role, and its removal.
  DASHBOARD_SHARED = "dashboard_shared",
  DASHBOARD_PERMISSION_CHANGED = "dashboard_permission_changed",
  DASHBOARD_ACCESS_REVOKED = "dashboard_access_revoked",
  ORGANIZATION_OWNERSHIP_RECEIVED = "organization_ownership_received",
  ORGANIZATION_OWNER_REMOVED = "organization_owner_removed",
  ORGANIZATION_LEFT = "organization_left",
  ORGANIZATION_DEACTIVATED = "organization_deactivated",
  PRESENTATION_THEME_READY = "presentation_theme_ready",
  PRESENTATION_THEME_FAILED = "presentation_theme_failed",
  USAGE_THRESHOLD_REACHED = "usage_threshold_reached",
  AGENT_AUTO_PAUSED = "agent_auto_paused",
  WEBHOOK_RUN_SUCCEEDED = "webhook_run_succeeded",
  WEBHOOK_RUN_FAILED = "webhook_run_failed",
  AGENT_RUN_FAILED = "agent_run_failed",
  WORKFLOW_RUN_FAILED = "workflow_run_failed",
  CONNECTOR_PERMISSION_CHANGED = "connector_permission_changed",
}

// NOTE: Mode mirrors apps/nsai/server/src/notifications/notifications.types.ts.
// DIRECT/PROJECT_ALL set targetProjectUuid (recipient must switch projects to
// view the file); PUBLIC leaves it null because the file is now org-wide.
export enum EKnowledgeShareMode {
  DIRECT = "direct",
  PROJECT_ALL = "project_all",
  PUBLIC = "public",
}

// NOTE: Buckets used by the per-category notification preference UI. Each
// ENotificationType maps to exactly one category server-side (see
// TYPE_TO_NOTIFICATION_CATEGORY in apps/nsai/server/src/notifications/
// notifications.types.ts). Adding a new ENotificationType only needs an
// entry in that map — the category enum stays stable so existing user
// preferences keep matching.
export enum ENotificationCategory {
  PROJECTS = "projects",
  ASSISTANTS = "assistants",
  SKILLS = "skills",
  CHATS = "chats",
  DASHBOARDS = "dashboards",
  MEETINGS = "meetings",
  MODELS = "models",
  PROMPTS = "prompts",
  PROBES = "probes",
  KNOWLEDGE = "knowledge",
  CALENDARS = "calendars",
  INTEGRATIONS = "integrations",
  REMINDERS = "reminders",
  ORGANIZATIONS = "organizations",
  USAGE_LIMITS = "usage_limits",
  AUTOMATIONS = "automations",
}

// NOTE: Wire shape stored in user_preferences.notification_preferences.
// Missing keys mean "enabled" — empty object == all categories on. Only
// explicit `false` skips emit. This makes default-on free (no backfill).
export type TNotificationCategoryPreferences = Partial<
  Record<ENotificationCategory, boolean>
>;

// NOTE: One data interface per notification type. Flat shapes (no nested
// objects). The discriminated mapping below ties each enum value to exactly
// one shape so callers can't pass arbitrary payloads.

export interface IProjectInviteNotificationData {
  inviterUserId: TEntityId;
  inviterName: string;
  organizationUuid: string;
  projectUuid: string;
  projectTitle: string;
  roleName: string;
}

export interface IProjectLeftNotificationData {
  leftUserId: TEntityId;
  leftUserName: string;
  organizationUuid: string;
  projectUuid: string;
  projectTitle: string;
}

export interface IProjectMemberRemovedNotificationData {
  removedByUserId: TEntityId;
  removedByName: string;
  organizationUuid: string;
  projectUuid: string;
  projectTitle: string;
}

export interface IProjectRoleUpdatedNotificationData {
  changedByUserId: TEntityId;
  changedByName: string;
  organizationUuid: string;
  projectUuid: string;
  projectTitle: string;
  newRoleName: string;
}

// NOTE: Self-notification payload emitted by the `notifySelf` chat tool. The
// recipient is always the calling user — the LLM cannot target anyone else.
// `body` is markdown (rendered by the card on each platform).
export interface ISelfReminderNotificationData {
  title: string;
  body: string;
  // NOTE: NSAI-6309 — the untruncated report behind the card's preview. `body`
  // is a single line so a multi-line report never renders half-broken in the
  // bell; this is the whole thing, revealed by the card's show-more toggle.
  // Absent when the preview is already the entire body, and on notifications
  // stored before this field existed.
  fullBody?: string;
  sourceChatUuid?: string;
  automationUuid?: string;
  // NOTE: Source automation's project. The View runs CTA switches into it
  // before deep-linking, since the Agents page resolves runs per project.
  projectUuid?: string;
}

// NOTE: Emitted from the meeting workflow callback when a meeting finishes
// processing successfully. Recipient is always the meeting owner.
export interface IMeetingProcessedNotificationData {
  meetingUuid: string;
  meetingTitle: string;
}

// NOTE: Emitted on any terminal failure status (recording / transcription /
// summarization / generic processor failure). `failureStatus` carries the
// specific status string so the card can show contextual copy. `isRetryable`
// lets the UI offer a Retry CTA only for processor-side failures — recorder
// failures cannot be replayed.
export interface IMeetingFailedNotificationData {
  meetingUuid: string;
  meetingTitle: string;
  failureStatus: string;
  isRetryable: boolean;
}

// NOTE: Emitted to the user a meeting was shared with. `sharerName` identifies
// who performed the share; `meetingTitle` lets the card render context.
export interface IMeetingSharedNotificationData {
  meetingUuid: string;
  meetingTitle: string;
  sharerUserId: TEntityId;
  sharerName: string;
  permissionLevel: string;
}

// NOTE: Emitted to the user whose meeting access was revoked. `revokerName`
// identifies who removed it. Self-removal does not emit.
export interface IMeetingAccessRevokedNotificationData {
  meetingUuid: string;
  meetingTitle: string;
  revokerUserId: TEntityId;
  revokerName: string;
}

// NOTE: Emitted when an OAuth-based calendar connection (Google / Microsoft)
// flips to expired — refresh has failed and the user must reconnect to keep
// syncing events. iCal connections do not refresh and never produce this.
// `providerLabel` is the brand name the server picked (Google / Outlook) so
// the frontend doesn't need its own provider → display-name mapping.
export interface ICalendarDisconnectedNotificationData {
  connectionUuid: string;
  provider: string;
  providerLabel: string;
  displayName: string | null;
  email: string | null;
}

// NOTE: Emitted when an OAuth-based connector (Google Drive, Slack, HubSpot,
// etc.) refresh fails — the recipient is the integration owner and must
// reconnect to keep tool calls flowing. `providerLabel` is the brand name the
// server picked so the frontend doesn't need its own provider → display-name
// mapping. `title` is the user-chosen integration row title (defaults to the
// provider label when the user didn't set one).
export interface IIntegrationDisconnectedNotificationData {
  integrationUuid: string;
  provider: EIntegrationProvider;
  providerLabel: string;
  title: string;
  projectUuid: string;
}

// NOTE: Emitted to every project member with MODEL_READ permission (except
// the creator themselves) when a new project-scoped model is added. The
// "Start chat" CTA deep-links to /chat?model=<uuid> which the chat composer
// uses to pre-select the model — same pattern used for assistants today.
export interface IModelCreatedNotificationData {
  modelUuid: string;
  modelTitle: string;
  modelName: string;
  creatorUserId: TEntityId;
  creatorName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted when a knowledge file becomes available to the recipient — via
// direct share, share-with-all-in-project, or going public. The notification
// card switches the recipient into `targetProjectUuid` (when set) before
// navigating to /knowledge so the file shows up under "Shared with me".
export interface IKnowledgeFileSharedNotificationData {
  fileUuid: string;
  fileName: string;
  actorUserId: TEntityId;
  actorName: string;
  shareMode: EKnowledgeShareMode;
  targetProjectUuid: string | null;
  targetProjectTitle: string | null;
}

// NOTE: Emitted to the users who lost access to a file. No targetProject fields
// — the recipient can no longer open it, so the card has no navigation CTA.
export interface IKnowledgeFileAccessRevokedNotificationData {
  fileUuid: string;
  fileName: string;
  actorUserId: TEntityId;
  actorName: string;
}

// NOTE: Emitted to every project member with PROMPT_LIBRARY_READ permission
// (except the creator) when a new prompt is added to a non-private folder.
// The "View prompts" CTA switches into projectUuid and navigates to the
// prompt-library page.
export interface IPromptCreatedNotificationData {
  promptUuid: string;
  promptTitle: string;
  creatorUserId: TEntityId;
  creatorName: string;
  projectUuid: string;
  projectTitle: string;
  folderUuid: string;
  folderName: string;
}

// NOTE: Emitted to every project member with PROBE_READ permission (except
// the creator) when a probe is created. The "View probes" CTA switches into
// projectUuid and navigates to the analytics page (probes live under the
// analytics screen).
export interface IProbeCreatedNotificationData {
  probeUuid: string;
  probeName: string;
  creatorUserId: TEntityId;
  creatorName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted when a non-temporary assistant is created. Recipients are the
// deduped union of: project members with PUBLIC_ASSISTANT_READ (public
// assistants only) and the organization admins (owners), who are notified for
// both public AND private creations so they keep oversight of what members
// build (NSAI-3829). The creator is always excluded.
export interface IAssistantCreatedNotificationData {
  assistantUuid: string;
  assistantName: string;
  creatorUserId: TEntityId;
  creatorName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted to the user a private assistant was shared with. Carries the
// granted permission level so the card can render "shared as viewer", etc.
export interface IAssistantSharedNotificationData {
  assistantUuid: string;
  assistantName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  permissionLevel: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted to the assistant owner when a non-owner collaborator removes
// themselves from a shared assistant.
export interface IAssistantLeftNotificationData {
  assistantUuid: string;
  assistantName: string;
  leftUserId: TEntityId;
  leftUserName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted to the affected user when the assistant owner changes their
// permission level on a private share.
export interface IAssistantPermissionChangedNotificationData {
  assistantUuid: string;
  assistantName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  permissionLevel: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted to the collaborator whose assistant access the owner revoked.
export interface IAssistantAccessRevokedNotificationData {
  assistantUuid: string;
  assistantName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted to the assistant owner when a non-owner edits a PRIVATE
// assistant they have editor access to. Public assistants don't emit this —
// any project member with public write can edit those, so it would be noisy.
// `changedFields` carries canonical field keys so each platform renders the
// field labels via its own i18n catalog.
export interface IAssistantEditedNotificationData {
  assistantUuid: string;
  assistantName: string;
  editorUserId: TEntityId;
  editorName: string;
  changedFields: string[];
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6952 — emitted when a PUBLIC skill is created, to the project
// members whose role holds public_skill:read. Unlike assistants, organization
// admins are not notified, and a private skill notifies nobody. The creator is
// always excluded.
export interface ISkillCreatedNotificationData {
  skillUuid: string;
  skillName: string;
  creatorUserId: TEntityId;
  creatorName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6952 — emitted to the project member a private skill was shared
// with. `permissionLevel` is the ESkillShareRole value granted ("viewer" /
// "editor"); each platform renders it through resolveSkillSharedBodyKey.
export interface ISkillSharedNotificationData {
  skillUuid: string;
  skillName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  permissionLevel: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6952 — emitted to the member whose skill share role the owner
// changed.
export interface ISkillPermissionChangedNotificationData {
  skillUuid: string;
  skillName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  permissionLevel: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6952 — emitted to the member whose skill share the owner revoked
// one by one. Bulk revocations (made public, archived, deleted) emit nothing.
export interface ISkillAccessRevokedNotificationData {
  skillUuid: string;
  skillName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6952 — emitted to the skill owner when an Editor share holder
// edits their private skill or replaces its files. `changedFields` carries the
// skill API's field names (plus "resources" for a file change); each platform
// labels them through resolveSkillEditedFieldLabelKey.
export interface ISkillEditedNotificationData {
  skillUuid: string;
  skillName: string;
  editorUserId: TEntityId;
  editorName: string;
  changedFields: string[];
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted to a user invited to a chat shared with specific people
// (NSAI-3247). Carries the share token so the card can deep-link to the
// read-only shared view.
export interface IChatSharedNotificationData {
  chatUuid: string;
  chatTitle: string;
  shareToken: string;
  ownerUserId: TEntityId;
  ownerName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: Emitted to a user whose specific-people access to a chat was revoked.
export interface IChatAccessRevokedNotificationData {
  chatUuid: string;
  chatTitle: string;
  ownerUserId: TEntityId;
  ownerName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6940 — emitted to a project member a dashboard was shared with.
// `permissionLevel` is the EDashboardShareRole value granted ("viewer" /
// "editor"); each platform renders it through resolveDashboardSharedBodyKey, so
// the card reads "Can view" / "Can edit" in the reader's language.
export interface IDashboardSharedNotificationData {
  dashboardUuid: string;
  dashboardName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  permissionLevel: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6940 — emitted to the member whose share role the owner changed.
export interface IDashboardPermissionChangedNotificationData {
  dashboardUuid: string;
  dashboardName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  permissionLevel: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6940 — emitted to the member whose share the owner removed.
export interface IDashboardAccessRevokedNotificationData {
  dashboardUuid: string;
  dashboardName: string;
  ownerUserId: TEntityId;
  ownerName: string;
  projectUuid: string;
  projectTitle: string;
}

// NOTE: NSAI-6940 — the per-role body keys of the dashboard share cards in the
// `notifications` namespace. Spelled out as literals so react-i18next's strict
// key typing accepts them without widening to `string`.
export type TDashboardSharedBodyKey =
  | "type.dashboard_shared.body_viewer"
  | "type.dashboard_shared.body_editor";

export type TDashboardPermissionChangedBodyKey =
  | "type.dashboard_permission_changed.body_viewer"
  | "type.dashboard_permission_changed.body_editor";

// NOTE: NSAI-6952 — the per-role body keys of the skill share cards, spelled
// out as literals for the same strict-typing reason as the dashboard ones.
export type TSkillSharedBodyKey =
  | "type.skill_shared.body_viewer"
  | "type.skill_shared.body_editor";

export type TSkillPermissionChangedBodyKey =
  | "type.skill_permission_changed.body_viewer"
  | "type.skill_permission_changed.body_editor";

// NOTE: NSAI-6952 — the skill fields a `skill_edited` notification can name,
// by the skill API's field names (the server's `changedFields` values).
export enum ESkillEditedField {
  ALLOWED_TOOLS = "allowedTools",
  ARGUMENT_HINT = "argumentHint",
  BODY = "body",
  COMPATIBILITY = "compatibility",
  DESCRIPTION = "description",
  IS_MODEL_INVOCATION_DISABLED = "isModelInvocationDisabled",
  LICENSE = "license",
  METADATA = "metadata",
  NAME = "name",
  RAW_FRONTMATTER = "rawFrontmatter",
  SOURCE_REVISION = "sourceRevision",
  SOURCE_TYPE = "sourceType",
  SOURCE_URL = "sourceUrl",
  IS_PUBLIC = "isPublic",
  IS_ENABLED = "isEnabled",
  IS_ARCHIVED = "isArchived",
  // NOTE: Not a content field — the server's label for a commit that replaced
  // the skill's files (PUT /skills/:skillUuid/resources).
  RESOURCES = "resources",
}

export type TSkillEditedFieldLabelKey =
  `type.skill_edited.fields.${ESkillEditedField}`;

// NOTE: NSAI-6952 — the five skill notification types. Each platform renders
// all of them with one card, from the view resolveSkillNotificationView builds,
// so the sentence and CTA rules live here once instead of in ten card files.
export type TSkillNotificationType =
  | ENotificationType.SKILL_CREATED
  | ENotificationType.SKILL_SHARED
  | ENotificationType.SKILL_PERMISSION_CHANGED
  | ENotificationType.SKILL_ACCESS_REVOKED
  | ENotificationType.SKILL_EDITED;

// NOTE: A discriminated union, so resolveSkillNotificationView narrows `data`
// by `type` without an assertion.
export type TSkillNotification = {
  [Type in TSkillNotificationType]: {
    type: Type;
    data: INotificationDataByType[Type];
  };
}[TSkillNotificationType];

export type TSkillNotificationBodyKey =
  | "type.skill_created.body"
  | TSkillSharedBodyKey
  | TSkillPermissionChangedBodyKey
  | "type.skill_access_revoked.body"
  | "type.skill_edited.body"
  | "type.skill_edited.body_no_fields";

export enum ESkillNotificationTone {
  ACCENT = "accent",
  DESTRUCTIVE = "destructive",
}

// NOTE: Everything a skill card renders except the platform's icon and
// layout. `changedFields` stays as raw field names: the card labels them with
// formatSkillEditedFields, because only the platform holds the `t` function.
export interface ISkillNotificationView {
  bodyKey: TSkillNotificationBodyKey;
  bodyParams: Record<string, string>;
  changedFields: string[];
  skillUuid: string;
  projectUuid: string;
  tone: ESkillNotificationTone;
  hasViewSkillCta: boolean;
}

export interface IFormatSkillEditedFieldsProps {
  changedFields: string[];
  translateLabel: (key: TSkillEditedFieldLabelKey) => string;
}

// NOTE: Emitted to the NEW owner after a successful ownership transfer.
// They now have project-admin role on every org project plus all
// organization-level controls (billing, member management, SAML, etc.).
export interface IOrganizationOwnershipReceivedNotificationData {
  organizationUuid: string;
  organizationName: string;
  previousOwnerUserId: TEntityId;
  previousOwnerName: string;
  isOwnershipTransferred?: boolean;
}

// NOTE: Emitted to the user whose owner role was revoked by another owner.
export interface IOrganizationOwnerRemovedNotificationData {
  organizationUuid: string;
  organizationName: string;
  removedByName: string;
}

// NOTE: Emitted to all remaining org owners when a member leaves the org.
export interface IOrganizationLeftNotificationData {
  organizationUuid: string;
  organizationName: string;
  memberName: string;
}

// NOTE: Emitted to all org owners (except the actor) when the organization
// is deactivated. Recipients need to understand why org actions stopped working.
export interface IOrganizationDeactivatedNotificationData {
  organizationUuid: string;
  organizationName: string;
  deactivatedByUserId: TEntityId;
  deactivatedByName: string;
}

// NOTE: Emitted to ALL org owners when a background presentation-theme
// template extraction finishes (NSAI-3862). `reason` is set on failures only;
// organizationUuid lets the card deep-link to the right appearance settings.
export interface IPresentationThemeExtractionNotificationData {
  organizationUuid: string;
  fileName: string;
  reason?: string;
}

// NOTE: `threshold` is the crossed percentage bucket (e.g. 80, 100). Org/project
// fields are null when the scope has no such context.
export interface IUsageThresholdReachedNotificationData {
  scope: ETokenBudgetScope;
  usageKind: ETokenBudgetUsageKind;
  threshold: number;
  period: TTokenLimitPeriod;
  usedTokens: number;
  limitTokens: number;
  resetAt: string;
  organizationUuid: string | null;
  organizationName: string | null;
  projectUuid: string | null;
  projectTitle: string | null;
  // NOTE: NSAI-5612 — the denomination `usedTokens` / `limitTokens` are in.
  // Absent on notifications stored before cost caps shipped, which are all
  // token caps. The figure names are historical for the same reason: this
  // payload is persisted on the notification row, so renaming them would blank
  // out every alert already in the table.
  unit?: ETokenBudgetUnit;
}

// NOTE: Authoritative map — every ENotificationType MUST appear here. Adding
// a new enum value without a corresponding entry is a TypeScript error.
// NOTE: Emitted to the agent's owner when a scheduled agent is auto-paused after
// too many consecutive failed runs (NSAI-4259). The owner is the only recipient
// — they alone can resume it. `lastError` is the server-truncated first line of
// the failing run's error, or null when the run failed without a message.
// `projectUuid` lets the "View runs" CTA switch into the agent's project before
// deep-linking, since the Agents page resolves `?runs=` per project.
export interface IAgentAutoPausedNotificationData {
  agentUuid: string;
  agentName: string;
  projectUuid: string;
  consecutiveErrors: number;
  lastError: string | null;
}

// NOTE: NSAI-3490 — emitted after every webhook run (both outcomes), so
// triggering a webhook is never silent. One shape for both
// WEBHOOK_RUN_SUCCEEDED/WEBHOOK_RUN_FAILED (mirrors
// IPresentationThemeExtractionNotificationData's success/failure pairing) —
// `error` is only set on failure, already truncated server-side.
export interface IWebhookRunResultNotificationData {
  webhookUuid: string;
  webhookName: string;
  projectUuid: string;
  durationSec: number;
  error: string | null;
}

// NOTE: NSAI-7884 — why an agent run failed. Mirrors the server's
// EAgentRunFailureReason; each reason has its own whole sentence on the card.
export enum EAgentRunFailureReason {
  TIMEOUT = "timeout",
  OUTPUT_CHECK_GAVE_UP = "output_check_gave_up",
  ERROR = "error",
}

export type TAgentRunFailedBodyKey =
  | "type.agent_run_failed.body_timeout"
  | "type.agent_run_failed.body_output_check_gave_up"
  | "type.agent_run_failed.body_error";

export type TWorkflowRunFailedBodyKey =
  | "type.workflow_run_failed.body"
  | "type.workflow_run_failed.body_paused";

// NOTE: NSAI-7884 — an agent run that failed (scheduled, "Run now" or started
// by another agent), sent to the agent's owner. Not sent for the run that
// auto-pauses the agent (AGENT_AUTO_PAUSED covers that one). `reason` travels as a plain string so an older client
// survives a reason the server adds later — it reads as the generic error
// sentence. `error` is the server-truncated first line, or null.
export interface IAgentRunFailedNotificationData {
  agentUuid: string;
  agentName: string;
  projectUuid: string;
  runUuid: string | null;
  reason: string;
  error: string | null;
}

// NOTE: NSAI-7884 — a workflow run that failed, sent to the
// workflow's owner. `wasAutoPaused` marks the run that also paused it.
export interface IWorkflowRunFailedNotificationData {
  workflowUuid: string;
  workflowName: string;
  projectUuid: string;
  runUuid: string | null;
  error: string | null;
  wasAutoPaused: boolean;
}

// NOTE: NSAI-7884 — the two run failures share one card per
// platform; this union lets it narrow `data` by `type`.
export type TRunFailedNotification =
  | {
      type: ENotificationType.AGENT_RUN_FAILED;
      data: IAgentRunFailedNotificationData;
    }
  | {
      type: ENotificationType.WORKFLOW_RUN_FAILED;
      data: IWorkflowRunFailedNotificationData;
    };

// NOTE: NSAI-7884 — what a run-failed card renders, resolved once for web and
// mobile. `automationName` fills the body sentence; `isWorkflow` picks the CTA.
export interface IRunFailedNotificationView {
  automationUuid: string;
  projectUuid: string;
  isWorkflow: boolean;
  titleKey: "type.agent_run_failed.title" | "type.workflow_run_failed.title";
  bodyKey: TAgentRunFailedBodyKey | TWorkflowRunFailedBodyKey;
  lastErrorKey:
    | "type.agent_run_failed.last_error"
    | "type.workflow_run_failed.last_error";
  automationName: string;
  error: string | null;
}

// NOTE: NSAI-4090 — emitted to every org member who has this connector
// connected when an org admin changes the organization-wide minimum approval
// level for it. The acting admin is excluded server-side. Levels arrive as
// EConnectorApprovalLevel values (not localized text) so each client renders
// them through its own connector-approval catalog; `previousMinLevel` is
// NEVER_ASK ("no minimum") when the connector had no org policy before.
// `projectUuid` is a project where the recipient actually holds the connection,
// so the card's CTA can switch into it before opening /integrations/:provider.
//
// NSAI-4978 — deliberately carries NO actor identity. The card is fanned
// out to every org member holding this connector, so naming the admin who made
// the change (or, via the old email fallback, leaking their address) disclosed
// who holds which access to people who have no need for it. The payload is the
// disclosure boundary, not just the copy: the fields are absent from the wire so
// a future card can't reintroduce the leak by rendering them.
export interface IConnectorPermissionChangedNotificationData {
  provider: EIntegrationProvider;
  providerLabel: string;
  organizationUuid: string;
  organizationName: string;
  previousMinLevel: EConnectorApprovalLevel;
  newMinLevel: EConnectorApprovalLevel;
  projectUuid: string;
}

export interface INotificationDataByType extends Record<
  ENotificationType,
  object
> {
  [ENotificationType.PROJECT_INVITE]: IProjectInviteNotificationData;
  [ENotificationType.PROJECT_LEFT]: IProjectLeftNotificationData;
  [ENotificationType.PROJECT_MEMBER_REMOVED]: IProjectMemberRemovedNotificationData;
  [ENotificationType.PROJECT_ROLE_UPDATED]: IProjectRoleUpdatedNotificationData;
  [ENotificationType.SELF_REMINDER]: ISelfReminderNotificationData;
  [ENotificationType.MEETING_PROCESSED]: IMeetingProcessedNotificationData;
  [ENotificationType.MEETING_FAILED]: IMeetingFailedNotificationData;
  [ENotificationType.MEETING_SHARED]: IMeetingSharedNotificationData;
  [ENotificationType.MEETING_ACCESS_REVOKED]: IMeetingAccessRevokedNotificationData;
  [ENotificationType.CALENDAR_DISCONNECTED]: ICalendarDisconnectedNotificationData;
  [ENotificationType.INTEGRATION_DISCONNECTED]: IIntegrationDisconnectedNotificationData;
  [ENotificationType.MODEL_CREATED]: IModelCreatedNotificationData;
  [ENotificationType.KNOWLEDGE_FILE_SHARED]: IKnowledgeFileSharedNotificationData;
  [ENotificationType.KNOWLEDGE_FILE_ACCESS_REVOKED]: IKnowledgeFileAccessRevokedNotificationData;
  [ENotificationType.PROMPT_CREATED]: IPromptCreatedNotificationData;
  [ENotificationType.PROBE_CREATED]: IProbeCreatedNotificationData;
  [ENotificationType.ASSISTANT_CREATED]: IAssistantCreatedNotificationData;
  [ENotificationType.ASSISTANT_SHARED]: IAssistantSharedNotificationData;
  [ENotificationType.ASSISTANT_LEFT]: IAssistantLeftNotificationData;
  [ENotificationType.ASSISTANT_PERMISSION_CHANGED]: IAssistantPermissionChangedNotificationData;
  [ENotificationType.ASSISTANT_ACCESS_REVOKED]: IAssistantAccessRevokedNotificationData;
  [ENotificationType.ASSISTANT_EDITED]: IAssistantEditedNotificationData;
  [ENotificationType.SKILL_CREATED]: ISkillCreatedNotificationData;
  [ENotificationType.SKILL_SHARED]: ISkillSharedNotificationData;
  [ENotificationType.SKILL_PERMISSION_CHANGED]: ISkillPermissionChangedNotificationData;
  [ENotificationType.SKILL_ACCESS_REVOKED]: ISkillAccessRevokedNotificationData;
  [ENotificationType.SKILL_EDITED]: ISkillEditedNotificationData;
  [ENotificationType.CHAT_SHARED]: IChatSharedNotificationData;
  [ENotificationType.CHAT_ACCESS_REVOKED]: IChatAccessRevokedNotificationData;
  [ENotificationType.DASHBOARD_SHARED]: IDashboardSharedNotificationData;
  [ENotificationType.DASHBOARD_PERMISSION_CHANGED]: IDashboardPermissionChangedNotificationData;
  [ENotificationType.DASHBOARD_ACCESS_REVOKED]: IDashboardAccessRevokedNotificationData;
  [ENotificationType.ORGANIZATION_OWNERSHIP_RECEIVED]: IOrganizationOwnershipReceivedNotificationData;
  [ENotificationType.ORGANIZATION_OWNER_REMOVED]: IOrganizationOwnerRemovedNotificationData;
  [ENotificationType.ORGANIZATION_LEFT]: IOrganizationLeftNotificationData;
  [ENotificationType.ORGANIZATION_DEACTIVATED]: IOrganizationDeactivatedNotificationData;
  [ENotificationType.PRESENTATION_THEME_READY]: IPresentationThemeExtractionNotificationData;
  [ENotificationType.PRESENTATION_THEME_FAILED]: IPresentationThemeExtractionNotificationData;
  [ENotificationType.USAGE_THRESHOLD_REACHED]: IUsageThresholdReachedNotificationData;
  [ENotificationType.AGENT_AUTO_PAUSED]: IAgentAutoPausedNotificationData;
  [ENotificationType.WEBHOOK_RUN_SUCCEEDED]: IWebhookRunResultNotificationData;
  [ENotificationType.WEBHOOK_RUN_FAILED]: IWebhookRunResultNotificationData;
  [ENotificationType.AGENT_RUN_FAILED]: IAgentRunFailedNotificationData;
  [ENotificationType.WORKFLOW_RUN_FAILED]: IWorkflowRunFailedNotificationData;
  [ENotificationType.CONNECTOR_PERMISSION_CHANGED]: IConnectorPermissionChangedNotificationData;
}

export type TNotificationData =
  INotificationDataByType[keyof INotificationDataByType];

// NOTE: Wire-friendly parsed shape sent by the server. Consumers that need
// to render type-specific UI should `switch (notification.type)` and pick the
// matching interface from INotificationDataByType for narrowing.
export interface IParsedNotification {
  uuid: string;
  type: ENotificationType;
  projectUuid: string | null;
  data: TNotificationData;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

// NOTE: Discriminated union view — for places that already know the type at
// the type level (e.g. inside a `case ENotificationType.X:` branch).
export type TParsedNotificationOfType<K extends ENotificationType> = Extract<
  IParsedNotification,
  { type: K }
> & {
  data: INotificationDataByType[K];
};

export interface IListNotificationsParams {
  limit?: number;
  offset?: number;
  onlyUnread?: boolean;
}

export interface IListNotificationsResponse {
  notifications: IParsedNotification[];
  hasMore: boolean;
}

export interface IUnreadCountResponse {
  count: number;
}

export interface IMarkNotificationAsReadParams {
  uuid: string;
}

export interface IMarkAsReadResponse {
  message: string;
}

// NOTE: NSAI-4293 — OS-level push notifications. Mirrors
// apps/nsai/server/src/types/push-notifications.types.ts and
// src/notifications/push.types.ts.
export enum EPushPlatform {
  IOS = "ios",
  ANDROID = "android",
}

export interface IRegisterPushTokenParams {
  token: string;
  platform: EPushPlatform;
  deviceName?: string;
}

export interface IRegisterPushTokenResponse {
  uuid: string;
}

export interface IUnregisterPushTokenParams {
  token: string;
}

export interface IUnregisterPushTokenResponse {
  message: string;
}

export interface INotificationCreatedEventPayload {
  notification: IParsedNotification;
}

// NOTE: Payload for the ENotificationWsEvent.MEETING_STATUS_CHANGED live-refresh
// signal. Emitted to the meeting owner on every status transition so the
// call-assistant list can invalidate + refetch instead of polling. Deliberately
// minimal — the client refetches the meetings query for the authoritative row
// rather than trusting the pushed status to patch cache across every
// paginated/filtered/sorted query key. Mirrors the server payload in
// apps/nsai/server/src/notifications/notifications.types.ts.
export interface IMeetingStatusChangedEventPayload {
  meetingUuid: string;
  status: EMeetingStatus;
}

// NOTE: Payload for ENotificationWsEvent.ORGANIZATION_ACCESS_CHANGED
// (NSAI-3616). Carries only the organization uuid — the client refetches the
// organizations/projects/members queries for the authoritative permissions
// rather than trusting a pushed flag. Mirrors the server payload in
// apps/nsai/server/src/notifications/notifications.types.ts.
export interface IOrganizationAccessChangedEventPayload {
  organizationUuid: string;
}

// NOTE: Wire shape of the Socket.IO `auth` handshake for the
// /api/notifications namespace. Identical on web and mobile because both
// hit the same gateway. `token` carries the Bearer-prefixed access token;
// `projectUuid` and `language` are nullable because the user may not have
// a current project selected and the language preference may not be set
// (server falls back to its default in both cases).
export interface INotificationsWsAuthPayload {
  token: string;
  projectUuid: string | null;
  language: string | null;
}

// NOTE: Escalating urgency buckets for the usage_threshold_reached card. Shared
// so web and mobile bucket a threshold identically; each app maps the tier onto
// its own colors, which is the only part that cannot be shared.
export enum EUsageThresholdSeverityTier {
  WARNING = "warning",
  ELEVATED = "elevated",
  CRITICAL = "critical",
  LIMIT_REACHED = "limit_reached",
}

// NOTE: `translate` is the caller's i18next `t` bound to the notifications
// namespace, typed as a plain lookup so this stays free of per-app i18next
// resource generics — same approach as
// IResolveConnectorPermissionChangedBodyProps below.
export interface IResolveUsageThresholdBodyProps {
  data: IUsageThresholdReachedNotificationData;
  translate: (key: string, values: Record<string, string>) => string;
}

export interface IResolveUsageThresholdPercentProps {
  usedTokens: number;
  limitTokens: number;
  threshold: number;
}

export interface IResolveUsageThresholdUnitProps {
  unit?: ETokenBudgetUnit;
}

// NOTE: `formatResetDistance` is injected because web and mobile are on
// different date-fns majors and each passes its own locale, so the distance
// phrase is formatted by the caller and only composed here.
export interface IBuildUsageThresholdMetaLineProps {
  usedTokens: number;
  limitTokens: number;
  resetAt: string;
  translate: (key: string, values: Record<string, string>) => string;
  formatResetDistance: (resetDate: Date) => string;
  // NOTE: Omitted means tokens — see IUsageThresholdReachedNotificationData.
  unit?: ETokenBudgetUnit;
}

// NOTE: Input to the shared connector_permission_changed body builder.
// `translate` is the caller's i18next `t` bound to the notifications namespace
// (it interpolates values), `translateApproval` the same bound to
// connectorApproval. Both are typed as plain lookups so this stays free of
// per-app i18next resource generics.
export interface IResolveConnectorPermissionChangedBodyProps {
  data: IConnectorPermissionChangedNotificationData;
  translate: (key: string, values: Record<string, string>) => string;
  translateApproval: (key: string) => string;
}
